import { useState, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Lookup, LookupOption } from '@/components/ui/lookup';
import { searchApi } from '@/lib/api/endpoints';
import type { SearchQuery, ApiSearchObject } from '@/lib/api/types';

interface SearchRecordLookupProps {
  value?: LookupOption<ApiSearchObject> | null;
  onChange: (value: LookupOption<ApiSearchObject> | null) => void;
  objectType?:
    | 'item'
    | 'entity'
    | 'collection'
    | 'initiative'
    | 'user'
    | Array<'item' | 'entity' | 'collection' | 'initiative' | 'user'>;
  context?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function SearchRecordLookup({
  value,
  onChange,
  objectType,
  context,
  placeholder = 'Search...',
  className,
  disabled,
}: SearchRecordLookupProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const queryFilters: SearchQuery = {
    query: searchQuery || undefined,
    objectType: objectType || undefined,
    context: context || undefined,
    limit: 20,
  };

  const objectTypeKey = Array.isArray(objectType) ? objectType.join(',') : objectType;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['search-record-lookup', searchQuery, objectTypeKey, context],
    queryFn: async ({ pageParam }) => {
      return searchApi.search({ ...queryFilters, nextCursor: pageParam });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    initialPageParam: '',
    staleTime: 1000 * 10,
  });

  const allResults = data?.pages.flatMap((page) => page.results) || [];

  const options: LookupOption<ApiSearchObject>[] = allResults.map((result) => ({
    id: result.id,
    label: result.name || `Record ${result.id}`,
    data: result,
  }));

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Lookup
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      options={options}
      isLoading={isLoading || isFetchingNextPage}
      hasMore={hasNextPage}
      placeholder={placeholder}
      emptyMessage="No results found"
      className={className}
      disabled={disabled}
    />
  );
}
